import {isString} from 'lodash';

/**
 * Contains text utils shared by server and client
 */

export const ZWJ = '&zwj;'; // zero width word joiner
// NOTE: We use String.fromCharCode instead of escaped unicode or string literals
// here because ascii holds up better across a hostile encoding environment
export const ZWJ_RAW = String.fromCharCode(8205);
export const NBSP = '&nbsp;';
export const NBSP_RAW = String.fromCharCode(160);
export const EMDASH = '—';

const ENTITY_ESCAPE_MAP = {
  [NBSP_RAW]: '&nbsp;',
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
} as const;
const ESCAPE_IN_TEXT_CHARS = Object.keys(ENTITY_ESCAPE_MAP);
const ESCAPE_IN_TEXT_REGEXP = new RegExp(
  '(' + ESCAPE_IN_TEXT_CHARS.join('|') + ')',
  'g'
);

const zwjEntities = [ZWJ, ZWJ_RAW];
// Match any non-ASCII character (including emojis)
const NON_ASCII = '[^\\u0000-\\u007F]';

// Regex components for ZWJ detection
const NEGATIVE_LOOKBEHIND = `(?<!${NON_ASCII})`; // Ensure not preceded by emoji
const NEGATIVE_LOOKAHEAD = `(?!${NON_ASCII})`; // Ensure not followed by emoji
const ZWJ_MATCHER = `(${zwjEntities.join('|')})`; // Match ZWJ entities

/**
 * Regex to match standalone ZWJ characters while preserving emoji sequences.
 *
 * Pattern breakdown:
 * 1. ${NEGATIVE_LOOKBEHIND}
 *    - Ensures the ZWJ is not preceded by an emoji (non-ASCII character)
 *
 * 2. ${ZWJ_MATCHER}
 *    - Matches either '&zwj;' or the raw ZWJ character (U+200D)
 *
 * 3. ${NEGATIVE_LOOKAHEAD}
 *    - Ensures the ZWJ is not followed by an emoji (non-ASCII character)
 *
 * Examples:
 * - "Hello‍World" -> "HelloWorld" (removes standalone ZWJ)
 * - "🏳️‍🌈" -> "🏳️‍🌈" (preserves Pride flag emoji)
 */
const zwjEntitySearch = new RegExp(
  `${NEGATIVE_LOOKBEHIND}${ZWJ_MATCHER}${NEGATIVE_LOOKAHEAD}`,
  'g'
);

const nonTextEntities = zwjEntities.concat(['<.*?>']);
const nonTextSearch = new RegExp(nonTextEntities.join('|'), 'g');
const nbspSearch = new RegExp(NBSP, 'g');
const zwjSearch = new RegExp([ZWJ, ZWJ_RAW].join('|'), 'g');
const whitespacePaddingSearch = /^\s*|\s*$/g;

/**
 * normalizes text content of a rich text element for validation
 * @param  {String} html html to normalize
 * @return {String}      [description]
 */
export function normalizeRichText(html: string): string {
  if (typeof html === 'string') {
    html = stripNonText(html);
    // The resulting textContent should be trimmed
    html = html.replace(whitespacePaddingSearch, '');
  }
  return html;
}

export function stripNonText(html: string): string {
  if (typeof html === 'string') {
    // Text fields may have alternating &nbsp; and space characters that should be convereted to spaces
    html = html.replace(nbspSearch, ' ');
    // Rich text fields will have html, and zwj characters that should be removed
    html = html.replace(nonTextSearch, '');
  }
  return html;
}

export function cleanNativeTextNodeValue(value: string): string {
  if (typeof value === 'string') {
    value = value.replace(zwjEntitySearch, '');
  }

  return value;
}

export function isEmptyValue(value: unknown): boolean {
  return !value || value === ZWJ_RAW || value === ZWJ;
}

export function isSpaceOrNbsp(value: string): boolean {
  return value === ' ' || value === NBSP_RAW;
}

/**
 * Replaces <, >, &, and unicode non-breaking spaces with their html entities
 * @param  {String} str text node value
 * @return {String}
 */
export function encodeTextValue(str: string): string {
  // @ts-expect-error - TS2769 - No overload matches this call.
  return str.replace(ESCAPE_IN_TEXT_REGEXP, function (unescapedChar) {
    return ENTITY_ESCAPE_MAP[unescapedChar];
  });
}

/**
 * Replaces " characters with &quot;
 * @param  {String} str
 * @return {String}
 */
export function encodeDoubleQuotes(str: string): string {
  return str.split('"').join('&quot;');
}

/**
 * Collapses multiple consecutive spaces into one.
 * @param  {String} str The original string, e.g. "bob the   builder"
 * @return {String}     The cleaned string, e.g. "bob the builder"
 */
export function collapseSpaces(str: string): string {
  if (!str || !isString(str)) {
    return str;
  }

  return str.replace(/\s{2,}/g, ' ');
}

/**
 * ensures that a value is either a single ZWJ character (an empty paragraph
 * node) or doesn't have any ZWJ characters (strays)
 */
export function cleanZWJChars(str: string) {
  if (typeof str === 'string') {
    return str.replace(zwjSearch, '') || ZWJ_RAW;
  }

  return str;
}

export function removeZWJChars(str: string): string {
  if (typeof str === 'string') {
    return str.replace(zwjSearch, '');
  }

  return str;
}

/**
 * Converts a human-entered block/component name to a CSS-compatible class name.
 * @param  {String} humanName The originally entered name of a selector token, e.g. "Hero Section"
 * @return {String}           The CSS-compatible version of that name, e.g. "hero-section"
 */
function getCssSafeName(humanName: string): string | null {
  let result = '';
  const name = humanName.toLowerCase();

  for (let i = 0; i < name.length; i++) {
    const code = name.charCodeAt(i);

    // 0-9, A-Z, -, _
    if (
      (code >= 48 && code <= 57) ||
      (code >= 97 && code <= 122) ||
      code === 45 ||
      code === 95
    ) {
      result += name[i];
    } else {
      result += ' ';
    }
  }

  if (result.length === 0) {
    return null;
  }

  result = result.replace(/\s{2,}|\s/g, '-').replace(/^[ _-]*|[ _-]*$/g, '');

  const first = result.charCodeAt(0);

  // If the class name starts with a number, then we need to prepend an underscore to make it valid
  // A dash (-) should be valid too, but Chrome barfs when trying to create a native selector with it.
  if (first >= 48 && first <= 57) {
    result = '_' + result;
    // Make sure user classes don't conflict with classes used by the designer
  } else if (/^wf?-/.test(result)) {
    result = '_' + result;
  }

  return result;
}

const cssifyCacheMap = new Map<string, string | null>();

export function cssifyName(humanName: string): string | null {
  let result = cssifyCacheMap.get(humanName);
  if (result === undefined) {
    result = getCssSafeName(humanName);
    cssifyCacheMap.set(humanName, result);
  }
  return result;
}
